import { webApi } from '@/shared/api/WebApi';
import type {
  DashboardUploaderCfg,
  ClientIngest,
  CreateVideoRequest,
} from '@/lib/types/Upload';
import type { IngestcfgData } from '@/lib/types/Ingest';

/**
 * Upload API endpoints.
 * Static class for consistent naming and namespacing.
 */
export class UploadApi {
  /**
   * Fetch S3 STS credentials for dashboard uploading.
   * GET /v1/ingest/dashboard-uploadercfg?cId={cId}&clientTS={ts}
   *
   * clientTS is the current Unix epoch seconds — backend uses it to calculate clock drift.
   */
  static async getDashboardUploaderCfg(cId: number): Promise<DashboardUploaderCfg> {
    const clientTS = Math.round(Date.now() / 1000);
    return webApi.get<DashboardUploaderCfg>('ingest/dashboard-uploadercfg', {
      cId,
      clientTS,
    });
  }

  /**
   * Fetch ingest configurations for a customer.
   * GET /v1/ingest/ingestcfgs?cId={cId}
   */
  static async getIngestcfgs(cId: number): Promise<IngestcfgData[]> {
    return webApi.get<IngestcfgData[]>('ingest/ingestcfgs', { cId });
  }

  /**
   * Create a video record for upload.
   * POST /v1/media/video?cId={cId}
   *
   * Returns ClientIngest with S3 key, ingestJobId, and mediaId.
   * opts.signal: AbortSignal forwarded from Uppy callbacks for clean cancellation.
   */
  static async createVideo(
    cId: number,
    req: CreateVideoRequest,
    opts?: { signal?: AbortSignal }
  ): Promise<ClientIngest> {
    return webApi.post<ClientIngest>('media/video', { cId }, req, {
      signal: opts?.signal,
    });
  }

  /**
   * Update media status to "ingesting" (called on first upload progress).
   * POST /v1/ingest/media/{mediaId}/ingesting?cId={cId}
   */
  static async updateStatusToIngesting(cId: number, mediaId: number): Promise<void> {
    await webApi.post<void>(`ingest/media/${mediaId}/ingesting`, { cId });
  }

  /**
   * Report upload progress to backend.
   * POST /v1/ingest/ingest-progress?cId={cId}&jobId={jobId}&rate={rate}
   *
   * rate is a percentage 0-100 (not bytes/sec).
   */
  static async updateProgress(
    cId: number,
    jobId: number,
    rate: number
  ): Promise<void> {
    await webApi.post<void>('ingest/ingest-progress', { cId, jobId, rate });
  }
}
